import { compact, uniq } from 'lodash';

import { getTimeFromApi } from '../../service/BannerHelpers/BannerHelpers';
import { BannersEditApiData, BannersEditApiDataCopy } from '../../typings/banners';
import { getEditedBannerWithOrganization } from '../../typings/bannersEditState';
import { DataForEditCampaigns, EditCampaignValueField } from '../../typings/campaignsEdit';
import { Lang } from '../../typings/common';
import {
    GdLanguageInput,
    OrganizationsQuery,
    OrganizationsQuery_organizations_workTimes
} from '../../typings/graphql-grid';
import { OrganizationData, OrganizationEditorParams } from '../../typings/organizations';
import { getValueFromApiFormat } from '../common/common';

export function mapOrganizationDataFromApiFormat(data: OrganizationsQuery): Record<string, OrganizationData> {
    return data.organizations.reduce((acc, organization) => {
        if (organization) {
            const address = getValueFromApiFormat(organization.address);
            const phone = getValueFromApiFormat(organization.phone);
            const websites = getValueFromApiFormat(organization.urls);
            const workTimes = getValueFromApiFormat(organization.workTimes);

            return {
                ...acc,
                [organization.permalinkId]: {
                    id: organization.permalinkId,
                    name: organization.companyName,
                    status: organization.statusPublish,
                    workTimes:
                        workTimes &&
                        workTimes.map((period: OrganizationsQuery_organizations_workTimes) => {
                            return {
                                week: period.daysOfWeek,
                                time: {
                                    start: getTimeFromApi(period.startTime.hour, period.startTime.minute),
                                    end: getTimeFromApi(period.endTime.hour, period.endTime.minute, true)
                                }
                            };
                        }),
                    address: address && {
                        country: getValueFromApiFormat(address.country),
                        city: getValueFromApiFormat(address.city),
                        street: getValueFromApiFormat(address.street),
                        house: getValueFromApiFormat(address.houseWithBuilding)
                    },
                    phone: phone && {
                        codeCountry: phone.countryCode || '',
                        codeRegion: phone.cityCode || '',
                        phoneNumber: phone.phoneNumber || '',
                        extension: phone.extension || ''
                    },
                    website: websites && websites[0],
                    rubric: getValueFromApiFormat(organization.rubric),
                    editUrl: getValueFromApiFormat(organization.cabinetUrl),
                    profileUrl: getValueFromApiFormat(organization.profileUrl)
                }
            };
        }

        return acc;
    }, {});
}

export function mapEditorKeysToOrganisations(
    params: OrganizationEditorParams[],
    organizations: Record<string, OrganizationData>
): Record<string, OrganizationData> {
    return params.reduce((acc, editorParams) => {
        const { editorKey, organizationId, suggestedOrganizationId } = editorParams;
        const someOrganizationId = organizationId || suggestedOrganizationId;
        return {
            ...acc,
            [editorKey]: someOrganizationId && organizations[someOrganizationId]
        };
    }, {});
}

export function mapBannersToOrganizationEditorParams(
    bannersEditData: BannersEditApiData | BannersEditApiDataCopy
): OrganizationEditorParams[] {
    const {
        banners,
        clientInfo: { isShowAutoAssignedOrganizationsEnabled }
    } = bannersEditData;
    return compact(
        Object.keys(banners).map(bannerId => {
            const textBanner = getEditedBannerWithOrganization(banners[bannerId]);

            if (textBanner) {
                return {
                    editorKey: mapEntityIdToEditorKey(bannerId, EntityType.banner),
                    organizationId: textBanner.permalinkId,
                    suggestedOrganizationId: isShowAutoAssignedOrganizationsEnabled
                        ? textBanner.suggestedPermalinkId
                        : undefined
                };
            }

            return undefined;
        })
    );
}

export function mapLangToApiFormat(lang: Lang): GdLanguageInput {
    switch (lang) {
        case Lang.ru:
            return GdLanguageInput.RU;
        case Lang.en:
            return GdLanguageInput.EN;
        case Lang.tr:
            return GdLanguageInput.TR;
        case Lang.uk:
            return GdLanguageInput.UK;
        default:
            return GdLanguageInput.RU;
    }
}

export enum EntityType {
    banner = 'banner',
    campaign = 'campaign'
}

export function mapEntityIdToEditorKey(entityId: string, entityType?: EntityType): string {
    switch (entityType) {
        case EntityType.banner:
            return `${EntityType.banner}-${entityId}`;
        case EntityType.campaign:
            return `${EntityType.campaign}-${entityId}`;
        default:
            return entityId;
    }
}

export function mapEditorKeyToEntityId(editorKey: string, entityType?: EntityType): string {
    switch (entityType) {
        case EntityType.banner:
            return editorKey.replace(`${EntityType.banner}-`, '');
        case EntityType.campaign:
            return editorKey.replace(`${EntityType.campaign}-`, '');
        default:
            return editorKey;
    }
}

export function extractUniqOrganizationIds(params: OrganizationEditorParams[]): string[] {
    return uniq(compact(params.map(p => p.organizationId || p.suggestedOrganizationId)));
}

export function mapEditorKeyToEntityType(editorKey: string): EntityType | undefined {
    const supportedTypes = [EntityType.campaign, EntityType.banner];
    const matchedTypes = supportedTypes.filter(entityType => editorKey.startsWith(entityType));

    return matchedTypes.length === 1 ? matchedTypes[0] : undefined;
}

export function mapCampaignsToOrganizationEditorParams(
    campaignsEditData: DataForEditCampaigns
): OrganizationEditorParams[] {
    const { campaigns } = campaignsEditData;
    return compact(
        Object.keys(campaigns).map(campaignId => {
            const campaign = campaigns[campaignId];
            const campaignData = campaign.value;

            if (campaign.enabledFields.organizationId) {
                return {
                    editorKey: mapEntityIdToEditorKey(campaignId, EntityType.campaign),
                    organizationId: campaignData[EditCampaignValueField.organizationId]
                };
            }

            return false;
        })
    );
}
